/* src/hooks/useWorkspaceNames.js — display names for workspace switcher */

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export function workspaceDisplayName(workspaceId, currentUserUid, namesMap) {
  if (!workspaceId) return 'Workspace';
  if (namesMap?.[workspaceId]) return namesMap[workspaceId];
  return workspaceId === currentUserUid
    ? 'My Workspace'
    : `Workspace ${workspaceId.slice(0, 8)}…`;
}

export function useWorkspaceNames(availableWorkspaces, currentUserUid, workspaceDetails) {
  const [workspaceNames, setWorkspaceNames] = useState({});

  useEffect(() => {
    if (!currentUserUid || !availableWorkspaces?.length) {
      setWorkspaceNames({});
      return undefined;
    }

    let cancelled = false;

    async function load() {
      const names = {};
      const detailById = {};
      (workspaceDetails || []).forEach((d) => {
        if (d?.id) detailById[d.id] = d;
      });

      for (const workspaceId of availableWorkspaces) {
        const fromDetails = detailById[workspaceId]?.name;
        if (fromDetails) {
          names[workspaceId] = fromDetails;
          continue;
        }

        let displayName = workspaceId === currentUserUid
          ? 'My Workspace'
          : `Workspace ${workspaceId.slice(0, 8)}…`;
        try {
          const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));
          if (workspaceDoc.exists()) {
            const workspaceData = workspaceDoc.data();
            if (workspaceData.name) {
              displayName = workspaceData.name;
            } else if (workspaceData.ownerId && workspaceData.ownerId !== currentUserUid) {
              const ownerDoc = await getDoc(doc(db, 'users', workspaceData.ownerId));
              if (ownerDoc.exists()) {
                const ownerEmail = ownerDoc.data().email;
                displayName = ownerEmail ? `${ownerEmail}'s Workspace` : displayName;
              }
            }
          }
        } catch (error) {
          console.error('[WORKSPACE NAMES] load error:', error);
        }
        names[workspaceId] = displayName;
      }
      if (!cancelled) setWorkspaceNames(names);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [availableWorkspaces, currentUserUid, workspaceDetails]);

  return workspaceNames;
}
